import { useSettings } from '../context/SettingsContext';

export const useThemeColors = () => {
  const { settings } = useSettings();
  const sidebarColor = settings?.sidebarColor || '#ECE7FB';


  const lightenColor = (hex, percent) => {
    if (!hex || hex === '#') return '#F3F1FB';
    const cleanHex = hex.startsWith('#') ? hex : `#${hex}`;
    const num = parseInt(cleanHex.replace("#", ""), 16);
    if (isNaN(num)) return '#F3F1FB';

    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, (num >> 8 & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return "#" + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
  };

  const darkenColor = (hex, percent) => {
    if (!hex || hex === '#') return '#DED5F7';
    const cleanHex = hex.startsWith('#') ? hex : `#${hex}`;
    const num = parseInt(cleanHex.replace("#", ""), 16);
    if (isNaN(num)) return '#DED5F7';

    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, (num >> 8 & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return "#" + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
  };

  // Relative (WCAG-style) luminance, used to decide whether a chosen
  // sidebar color needs light or dark text sitting on top of it — a fixed
  // dark text color reads fine on the default lavender but goes invisible
  // the moment someone picks a deep/saturated preset.
  const getLuminance = (hex) => {
    if (!hex || hex === '#') return 1;
    const cleanHex = hex.startsWith('#') ? hex : `#${hex}`;
    const num = parseInt(cleanHex.replace('#', ''), 16);
    if (isNaN(num)) return 1;
    const toLinear = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const r = toLinear((num >> 16) & 0xFF);
    const g = toLinear((num >> 8) & 0xFF);
    const b = toLinear(num & 0xFF);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const sidebarIsDark = getLuminance(sidebarColor) < 0.45;

  // Overlay-based, not hue-based: a translucent black/white wash keeps
  // reasonable contrast against any hue at a given lightness, so this
  // holds up for whichever color is picked, not just the presets on offer.
  const sidebarTextColors = sidebarIsDark ? {
    text: 'rgba(255,255,255,0.82)',
    textMuted: 'rgba(255,255,255,0.56)',
    active: '#FFFFFF',
    hover: 'rgba(255,255,255,0.12)',
    border: 'rgba(255,255,255,0.16)'
  } : {
    text: 'rgba(30,28,46,0.78)',
    textMuted: 'rgba(30,28,46,0.55)',
    active: '#1E1C2E',
    hover: 'rgba(30,28,46,0.07)',
    border: 'rgba(30,28,46,0.12)'
  };

  return {
    sidebarColor,
    sidebarLight: lightenColor(sidebarColor, 20),
    sidebarDark: darkenColor(sidebarColor, 15),
    sidebarIsDark,
    sidebarTextColors,
    lightenColor,
    darkenColor
  };
};